// moment-timezone rather than moment: same object, plus the `.tz()` a reader in
// another zone than the browser's needs. Every existing filter is unaffected.
import moment from 'moment-timezone';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { activeCountry, activeCurrency, activeLocale } from '../utils/money';

// String prototype extensions (keep for backwards compatibility)
if (!String.prototype.lpad) {
    String.prototype.lpad = function (padString, length) {
        let str = this;
        while (str.length < length) str = padString + str;
        return str;
    };
}

if (!String.prototype.capitalize) {
    String.prototype.capitalize = function () {
        return this.charAt(0).toUpperCase() + this.slice(1);
    };
}

if (!String.prototype.toTime) {
    String.prototype.toTime = function () {
        let hour = String(Math.floor(this / 60));
        let min = String(this % 60);
        return hour.lpad('0', 2) + ':' + min.lpad('0', 2);
    };
}

// Parse date value: date-only strings (YYYY-MM-DD) stay local, datetimes convert from UTC
function parseDate(value) {
    if (!value) return moment();
    return /^\d{4}-\d{2}-\d{2}$/.test(value) ? moment(value) : moment.utc(value).local();
}

// Export filters as an object for use with $filters
const filters = {
    timeago(value) {
        return parseDate(value).fromNow();
    },

    fullname(obj) {
        if (obj && obj.firstname && obj.lastname) {
            return obj.firstname + ' ' + obj.lastname;
        }
        return obj?.email || '';
    },

    name(obj) {
        if (obj && obj.firstname) {
            return obj.firstname + ' ' + obj.lastname[0] + '.';
        }
        return obj?.email || '';
    },

    decimal(value) {
        return new Intl.NumberFormat(activeLocale(), {
            style: 'decimal',
            minimumFractionDigits: 2
        }).format(value || 0);
    },

    /**
     * Line-item quantity: decimals are allowed but never padded,
     * so 10.0000 shows as "10" and 2.5000 as "2.5".
     */
    quantity(value, maxDecimals = 4) {
        const number = parseFloat(value);
        if (isNaN(number)) return '';

        return new Intl.NumberFormat(activeLocale(), {
            style: 'decimal',
            minimumFractionDigits: 0,
            maximumFractionDigits: maxDecimals
        }).format(number);
    },

    /**
     * A rate or a probability held as a whole number: 25 or "25.00" both show
     * as "25 %". Intl works from a fraction, hence the division, and it gives
     * each locale its own spacing — "25 %" in fr-CA, "25%" in en-CA.
     */
    percent(value, maxDecimals = 2) {
        const number = parseFloat(value);
        if (isNaN(number)) return '';

        return new Intl.NumberFormat(activeLocale(), {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: maxDecimals
        }).format(number / 100);
    },

    bytes(value) {
        const sufixes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        const i = Math.floor(Math.log(value) / Math.log(1024));
        return (!value && '0 Bytes') || (value / Math.pow(1024, i)).toFixed(2) + ' ' + sufixes[i];
    },

    shortdate(value) {
        if (!value) return '';
        return parseDate(value).format('YYYY-MM-DD');
    },

    /**
     * « 16 oct. » — the day and the month, for somewhere with no room for a
     * year: a board card, a chip, a dense row. The year is added back when the
     * date is not in the current one, because "16 oct." two years out is a
     * date nobody reads correctly.
     */
    daymonth(value) {
        if (!value) return '';

        const local = parseDate(value);

        return local.format(local.year() === moment().year() ? 'D MMM' : 'D MMM YYYY');
    },

    /**
     * « 27 septembre 2026 » — the month written out, and the year always.
     *
     * For a date that is read once and stands on its own: when a record was
     * opened, when an agreement was signed, the line closing a feed. The
     * opposite call from `daymonth`, which abbreviates because it sits in a
     * dense row — here there is room, and nothing around the date to date it.
     */
    longdate(value) {
        if (!value) return '';

        return parseDate(value).format('D MMMM YYYY');
    },

    shortdateortime(value) {
        if (!value) return '';
        const now = moment();
        const local = parseDate(value);

        if (now.format('YYYY MM DD') !== local.format('YYYY MM DD')) {
            return local.format('D MMM YYYY');
        } else {
            return local.format('h:mm A');
        }
    },

    datetime(value) {
        if (!value) return '';
        return parseDate(value).format('D MMM YYYY, HH:mm');
    },

    /**
     * "Aujourd’hui à 08:05", "Hier à 23:54", "lundi dernier à 17:50", then a
     * plain date — moment's own calendar wording, in the app's locale.
     *
     * Absolute on purpose, where `timeago` is relative. A relative label has to
     * be re-rendered to stay true, and it answers the wrong question for
     * something that waited: "il y a 8 minutes" tells you how long you were
     * away, "08:42" tells you when the mail landed.
     *
     * @param {String} value - UTC instant, or a date-only YYYY-MM-DD
     * @param {String} [zone] - IANA zone to read it in; the browser's if omitted
     */
    calendar(value, zone = null) {
        if (!value) return '';

        // A date-only value has no instant to convert — shifting it would move
        // it a day in either direction.
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return moment(value).calendar();
        }

        const local = moment.utc(value);

        return (zone && moment.tz.zone(zone) ? local.tz(zone) : local.local()).calendar();
    },

    money(value, currencyCode, showZeroWhenNull = true) {
        // Backwards compat: second arg used to be showZeroWhenNull (boolean)
        if (typeof currencyCode === 'boolean') {
            showZeroWhenNull = currencyCode;
            currencyCode = null;
        }

        if (showZeroWhenNull === false && (value === null || value === undefined)) return '';

        const currency = currencyCode || activeCurrency();

        return new Intl.NumberFormat(activeLocale(), {
            style: 'currency',
            currency,
            minimumFractionDigits: 2
        }).format(value || 0);
    },

    /**
     * The same amount, short enough for a headline: "1,86 M$", "246 k$".
     *
     * A pipeline total is read at a glance and compared to the one beside it, so
     * the cents are noise — but only in a headline. Anything a person has to
     * reconcile keeps {@see money}, and every compact figure carries the full
     * one in its `title` so it is one hover away.
     */
    compactMoney(value) {
        const number = parseFloat(value) || 0;

        const currency = activeCurrency();

        return new Intl.NumberFormat(activeLocale(), {
            style: 'currency',
            currency,
            notation: 'compact',
            // Under a thousand the compact notation shows the number itself, and
            // dropping its decimals would turn 250,50 $ into 251 $.
            maximumFractionDigits: Math.abs(number) < 1000 ? 2 : 0
        }).format(number);
    },

    /**
     * A stored number, printed the way a person writes it.
     *
     * The phone control stores E.164 ("+15145550199") so the value stays
     * unambiguous wherever it travels — an SMS gateway, a webhook, an office in
     * another country. Nobody reads it that way, so the display puts it back:
     * national for a number from the organization's own country, international
     * for any other, where the country code is the part that matters.
     *
     * Anything unparseable is returned untouched: a number typed before this
     * control existed is still the best answer we have.
     */
    phone(value) {
        if (!value) {
            return value;
        }

        // Read through utils/money, which the app hands the organization to.
        // The lazy `require()` that used to stand here threw on every call in
        // an ES bundle and was swallowed, so every number was formatted as
        // Canadian whatever the organization said.
        const home = activeCountry();

        const parsed = parsePhoneNumberFromString(String(value));

        if (!parsed) {
            return value;
        }

        return parsed.country === home ? parsed.formatNational() : parsed.formatInternational();
    },

    countryTrans(key) {
        const locale = window.i18n?.locale?.value || 'en';
        const messages = window.i18n?.messages?.value || {};
        if (messages[locale]?.countries?.[key]) {
            return messages[locale].countries[key];
        } else if (messages['en']?.countries?.[key]) {
            return messages['en'].countries[key];
        }
        return key;
    },

    langTrans(key) {
        const locale = window.i18n?.locale?.value || 'en';
        const messages = window.i18n?.messages?.value || {};
        if (messages[locale]?.languages?.[key]) {
            return messages[locale].languages[key];
        } else if (messages['en']?.languages?.[key]) {
            return messages['en'].languages[key];
        }
        return key;
    },

    capitalize(value) {
        if (!value) return '';
        return value.toString().capitalize();
    },

    isBirthdayToday(birthday) {
        if (!birthday) return false;
        const today = new Date();
        const parts = birthday.split('-');
        return parseInt(parts[1]) === today.getMonth() + 1 && parseInt(parts[2]) === today.getDate();
    }
};

export default filters;
